import {createApi, fetchBaseQuery} from '@reduxjs/toolkit/query/react'
import {CalculatedEmissionsBackendResponse, CalculateEmissionsRequest,} from "../../api/model/emissions";
import {
    ChangeUsersAgencyRequest,
    DeleteUser,
    InviteCompanyAdminRequest,
    InviteUserRequest,
    ReadUsersWindowedRequest,
    RemoveUserFromAgency,
    UserAdminRequest,
    UserProps,
    UserRequest,
    WindowedUsersResponse
} from "../../api/model/user";
import {CompanyProps, CreateCompanyRequest, UpdateCompanyRequest} from "../../api/model/company";
import Cookies from "js-cookie";
import {
    AgencyProps,
    CreateAgencyRequest,
    GetAgenciesForGivenUserResponse,
    ReadAgenciesWindowedRequest,
    UpdateAgencyRequest,
    UploadLogoRequest,
    WindowedAgenciesResponse
} from "../../api/model/agency";
import {sortBy} from "lodash"
import {BASE_ENDPOINT, GET_AIRLINES} from "../../api/endpoints";
import {GENERAL_BACKEND_RESPONSE, GeneralBackendResponse} from "../../api/model/common";
import {getAgencyLogo} from "../../utils/getter/get-agency-logo";
import {setAgencies, setAgencyLogo, setAllUsersForAdmin, setCompany, setUserData} from "./user";
import {HotelResponse} from "../../api/model/activity-hotel";
import {Customer, SaveBundleCustomerRequest} from "../../api/model/customer";
import {setApiError, setApiSuccess} from "./settings";
import {ThunkDispatch} from "@reduxjs/toolkit";
import {
    BundleForStore,
    CreateBundleProps,
    LoadBundleResponse,
    ReadBundleWindowedRequest,
    TransformedWindowedBundleResponse,
    UpdateBundle,
    WindowedBundleResponse
} from "../../api/model/bundle";
import {isGeneralBackendResponseWithErrorCode} from "../../utils/check/type-guards";
import {COOKIE_NAME} from "../../constants/default-values";
import {AirlineResponse} from "../../api/model/activity-flight";
import {
    generateResendInvitationEmailUrl,
    transformBackendBundleToFrontEndBundle,
    transformFrontEndBundleToBackendBundle
} from "./api-utils";
import {DateTime} from "luxon";
import {roundCo2E} from "../../utils/math/round-values";
import {
    AcrissCodeRequest,
    CarCalculatorPropsResponse,
    CarPropsViaAcrissCodeResponse,
    GetAddressWithCoordinatesRequest,
    GetAdressWithCoordinatesResponse,
    GetComputedTotalPassengerCountRequest,
    GetComputedTotalPassengerCountResponse,
    GetRouteFromCoordinatesRequest,
    GetRouteFromCoordinatesResponse,
    ValidateAcrissCodeResponse
} from "../../api/model/activity-car-bus";

const polyline = require('@mapbox/polyline');

enum SUCCESS_TYPES {
    UPDATE_USER_ADMIN,
    MAKE_USER_TO_AGENCY_ADMIN,
    UNMAKE_USER_FROM_AGENCY_ADMIN,
    UPDATE_AGENCY,
    INVITE_COMPANY_ADMIN,
    INVITE_USER,
    DELETE_USER,
    DELETE_AGENCY,
    SET_AGENCY_LOGO,
    DELETE_AGENCY_LOGO,
    CHANGE_USERS_AGENCY,
    CHANGE_PASSWORD_EMAIL,
    CREATE_BUNDLE,
    UPDATE_BUNDLE,
    DELETE_BUNDLE,
}

enum ERROR_TYPES {
    UPDATE_USER_SELF,
    UPDATE_USER_ADMIN,
    MAKE_USER_TO_AGENCY_ADMIN,
    UNMAKE_USER_FROM_AGENCY_ADMIN,
    CREATE_COMPANY,
    UPDATE_COMPANY,
    CREATE_AGENCY,
    UPDATE_AGENCY,
    INVITE_COMPANY_ADMIN,
    INVITE_USER,
    DELETE_USER,
    DELETE_AGENCY,
    SET_AGENCY_LOGO,
    DELETE_AGENCY_LOGO,
    CHANGE_USERS_AGENCY,
    CHANGE_PASSWORD_EMAIL,
    CREATE_BUNDLE,
    READ_BUNDLE,
    LOAD_BUNDLE,
    UPDATE_BUNDLE,
    DELETE_BUNDLE,
    GET_ADDRESS_WITH_COORDINATES,
    ROUTE_FROM_COORDINATES,
    GENERAL_MAPBOX_ERROR,
    CALCULATE_EMISSIONS,
    GET_HOTEL_BY_GIATA_ID,
    VALIDATE_ACRISS_CODE,
    CAR_PROPS_VIA_ACRISS_CODE,
    COMPUTED_TOTAL_PASSENGER_COUNT
}

const generalSuccessHandling = (response: GeneralBackendResponse, dispatch: ThunkDispatch<any, any, any>, successType: SUCCESS_TYPES) => {
    if (response.status === GENERAL_BACKEND_RESPONSE.success) {
        let message = ''
        switch (successType) {
            case SUCCESS_TYPES.UPDATE_USER_ADMIN:
                message = "UPDATE_USER_SUCCESS_ADMIN"
                break
            case SUCCESS_TYPES.MAKE_USER_TO_AGENCY_ADMIN:
                message = "MAKE_USER_AGENCY_ADMIN"
                break;
            case SUCCESS_TYPES.UNMAKE_USER_FROM_AGENCY_ADMIN:
                message = "UNMAKE_USER_AGENCY_ADMIN"
                break;
            case SUCCESS_TYPES.UPDATE_AGENCY:
                message = "EDIT_AGENCY"
                break;
            case SUCCESS_TYPES.INVITE_COMPANY_ADMIN:
                message = "INVITE_COMPANY_ADMIN"
                break;
            case SUCCESS_TYPES.INVITE_USER:
                message = "INVITE_USER"
                break
            case SUCCESS_TYPES.DELETE_USER:
                message = "DELETE_USER"
                break;
            case SUCCESS_TYPES.DELETE_AGENCY:
                message = "DELETE_AGENCY"
                break
            case SUCCESS_TYPES.SET_AGENCY_LOGO:
                message = "UPLOAD_AGENCY_LOGO"
                break;
            case SUCCESS_TYPES.DELETE_AGENCY_LOGO:
                message = "DELETE_AGENCY_LOGO"
                break;
            case SUCCESS_TYPES.CHANGE_USERS_AGENCY:
                message = "CHANGE_USERS_AGENCY"
                break;
            case SUCCESS_TYPES.CHANGE_PASSWORD_EMAIL:
                message = "SEND_PASSWORD_RESET_EMAIL"
                break;
            case SUCCESS_TYPES.CREATE_BUNDLE:
                message = "CREATE_BUNDLE"
                break;
            case SUCCESS_TYPES.UPDATE_BUNDLE:
                message = "UPDATE_BUNDLE"
                break;
            case SUCCESS_TYPES.DELETE_BUNDLE:
                message = "DELETE_BUNDLE"
                break;
            default:
                message = ""
                successType satisfies never;
        }

        dispatch(setApiSuccess({
            toastType: "success",
            message: `TOASTS.SUCCESS.${message}`
        }))
    }

}

const handleSpecificErrors = (error: unknown) => {
    if (typeof error === "object" && error !== null && "error" in error) {
        const {error: errorResponse} = error
        if (typeof errorResponse === "object" && errorResponse !== null) {
            if ("data" in errorResponse) {
                const {data} = errorResponse
                if (isGeneralBackendResponseWithErrorCode(data)) {
                    const {errorCode} = data
                    return errorCode
                    // if (errorCode && errorCode in relevantErrorCodes) message = `ERRORS.BACKEND_ERROR_CODES.CODE_${errorCode}`
                }
            }
        }
    }
}

const generalErrorHandling = (error: unknown, dispatch: ThunkDispatch<any, any, any>, errorType: ERROR_TYPES) => {
    let message: string | null = null
    switch (errorType) {
        case ERROR_TYPES.UPDATE_USER_SELF:
            message = "UPDATE_USER_SELF"
            break
        case ERROR_TYPES.UPDATE_USER_ADMIN:
            message = "UPDATE_USER_ADMIN"
            break
        case ERROR_TYPES.MAKE_USER_TO_AGENCY_ADMIN:
            message = "MAKE_USER_ADMIN"
            break;
        case ERROR_TYPES.UNMAKE_USER_FROM_AGENCY_ADMIN:
            message = "UNSET_USER_ADMIN"
            break;
        case ERROR_TYPES.CREATE_COMPANY:
            message = "ADD_COMPANY"
            break;
        case ERROR_TYPES.UPDATE_COMPANY:
            message = "EDIT_COMPANY"
            break;
        case ERROR_TYPES.CREATE_AGENCY:
            message = "ADD_AGENCY"
            break;
        case ERROR_TYPES.UPDATE_AGENCY:
            message = "EDIT_AGENCY"
            break;
        case ERROR_TYPES.INVITE_COMPANY_ADMIN: {
            const commonMessage = "INVITE_COMPANY_ADMIN"
            const possibleErrorCode = handleSpecificErrors(error)
            if (typeof possibleErrorCode !== 'undefined') {
                if (possibleErrorCode === 506) {
                    message = 'INVITE_COMPANY_ADMIN_ALREADY_MEMBER'
                } else if (possibleErrorCode === 509) {
                    message = 'INVITE_COMPANY_ADMIN_COMPANY_EXISTS'
                } else {
                    message = commonMessage
                }
            } else {
                message = commonMessage
            }
            break;
        }
        case ERROR_TYPES.INVITE_USER: {
            const commonMessage = "INVITE_USER"
            const possibleErrorCode = handleSpecificErrors(error)
            if (typeof possibleErrorCode !== 'undefined') {
                if (possibleErrorCode === 518) {
                    message = 'INVITE_USER_DEACTIVATED'
                } else {
                    message = commonMessage
                }
            } else {
                message = commonMessage
            }
            break;
        }
        case ERROR_TYPES.DELETE_USER:
            message = "DELETE_USER"
            break;
        case ERROR_TYPES.DELETE_AGENCY:
            message = "DELETE_AGENCY"
            break
        case ERROR_TYPES.SET_AGENCY_LOGO:
            message = "UPLOAD_AGENCY_LOGO"
            break;
        case ERROR_TYPES.DELETE_AGENCY_LOGO:
            message = "DELETE_AGENCY_LOGO"
            break;
        case ERROR_TYPES.CHANGE_USERS_AGENCY:
            message = "CHANGE_USERS_AGENCY"
            break;
        case ERROR_TYPES.CHANGE_PASSWORD_EMAIL:
            message = "SEND_PASSWORD_RESET_EMAIL"
            break;
        case ERROR_TYPES.CREATE_BUNDLE:
            message = "CREATE_BUNDLE"
            break;
        case ERROR_TYPES.READ_BUNDLE:
            message = "READ_BUNDLE"
            break;
        case ERROR_TYPES.LOAD_BUNDLE:
            message = "LOAD_BUNDLE"
            break;
        case ERROR_TYPES.UPDATE_BUNDLE:
            message = "UPDATE_BUNDLE"
            break;
        case ERROR_TYPES.DELETE_BUNDLE:
            message = "DELETE_BUNDLE"
            break;
        case ERROR_TYPES.GET_ADDRESS_WITH_COORDINATES:
            message = "GET_ADDRESS_WITH_COORDINATES"
            break;
        case ERROR_TYPES.ROUTE_FROM_COORDINATES:
            message = "GET_ROUTE_FROM_COORDINATES"
            break;
        case ERROR_TYPES.GENERAL_MAPBOX_ERROR:
            message = "GENERAL"
            break;
        case ERROR_TYPES.CALCULATE_EMISSIONS:
            // handled in Choose<Activity>, this is only fallback
            break;
        case ERROR_TYPES.GET_HOTEL_BY_GIATA_ID:
            break;
        case ERROR_TYPES.VALIDATE_ACRISS_CODE:
            break;
        case ERROR_TYPES.CAR_PROPS_VIA_ACRISS_CODE:
            break;
        case ERROR_TYPES.COMPUTED_TOTAL_PASSENGER_COUNT:
            break;
        default:
            message = "GENERAL"
            errorType satisfies never;
    }

    if (message === null) {
        message = "ERRORS.GENERAL_NETWORK_ERROR_BODY"
    } else {
        message = `TOASTS.ERROR.${message}`
    }

    dispatch(setApiError({
        toastType: "error",
        message
    }))
}

export const apiSlice = createApi({
    reducerPath: 'api',
    tagTypes: ['User', 'Agencies', 'Company', 'KlimalinkAdminAgencies', 'KlimaLinkAdminUsers'],
    baseQuery: fetchBaseQuery({
        baseUrl: BASE_ENDPOINT,
        prepareHeaders: headers => {
            const token = Cookies.get(COOKIE_NAME)
            if (token) headers.set('authorization', `Bearer ${token}`);
            return headers;
        },
    }),
    endpoints: builder => ({
        // Calculation
        calculateEmissions: builder.mutation<CalculatedEmissionsBackendResponse, CalculateEmissionsRequest>({
            query: payload => ({
                method: 'POST',
                signal: new AbortController().signal,
                body: payload,
                url: "/api/v1/calculateEmissionFrontend",
                headers: {
                    'Content-Type': 'application/json',
                }
            }),
            async onQueryStarted(_, {queryFulfilled, dispatch}) {
                try {
                } catch (error) {
                    generalErrorHandling(error, dispatch, ERROR_TYPES.CALCULATE_EMISSIONS)
                }
            },
        }),

        // Users, Agency, Company
        resendInvitationEmail: builder.mutation<GeneralBackendResponse, { email: string, agencyId: number | null }>({
            query: payload => ({
                method: "POST",
                url: generateResendInvitationEmailUrl(payload.email, payload.agencyId),
            }),
        }),
        updateUserInfoSelf: builder.mutation<GeneralBackendResponse, UserRequest>({
            query: payload => ({
                method: 'PUT',
                body: payload,
                url: "/api/v1/userInfo",
                headers: {
                    'Content-Type': 'application/json',
                }
            }),
            invalidatesTags: ['User', 'Agencies'],
            async onQueryStarted(_, {queryFulfilled, dispatch}) {
                try {
                } catch (error) {
                    generalErrorHandling(error, dispatch, ERROR_TYPES.UPDATE_USER_SELF)
                }
            },
        }),
        updateUserInfoAdmin: builder.mutation<GeneralBackendResponse, {
            userObject: UserRequest,
            userEmail: string
        }>({
            query: payload => ({
                method: 'PUT',
                body: payload.userObject,
                url: `/api/v1/userInfo/${payload.userEmail}`,
                headers: {
                    'Content-Type': 'application/json',
                }
            }),
            invalidatesTags: ['Agencies', 'KlimalinkAdminAgencies', 'KlimaLinkAdminUsers'],
            async onQueryStarted(_, {queryFulfilled, dispatch}) {
                try {
                    const response = await queryFulfilled
                    generalSuccessHandling(response.data, dispatch, SUCCESS_TYPES.UPDATE_USER_ADMIN)
                } catch (error) {
                    generalErrorHandling(error, dispatch, ERROR_TYPES.UPDATE_USER_ADMIN)
                }
            },
        }),
        makeUserToAgencyAdmin: builder.mutation<GeneralBackendResponse, UserAdminRequest>({
            query: payload => ({
                method: 'POST',
                body: payload,
                url: `/api/v1/agency/${payload.agencyId}/admin/${payload.userEmail}`,
                headers: {
                    'Content-Type': 'application/json',
                }
            }),
            invalidatesTags: ['Agencies', "KlimaLinkAdminUsers"],
            async onQueryStarted(_, {queryFulfilled, dispatch}) {
                try {
                    const response = await queryFulfilled
                    generalSuccessHandling(response.data, dispatch, SUCCESS_TYPES.MAKE_USER_TO_AGENCY_ADMIN)
                } catch (error) {
                    generalErrorHandling(error, dispatch, ERROR_TYPES.MAKE_USER_TO_AGENCY_ADMIN)
                }
            },
        }),
        unmakeUserFromAgencyAdmin: builder.mutation<GeneralBackendResponse, UserAdminRequest>({
            query: payload => ({
                method: 'DELETE',
                body: payload,
                url: `/api/v1/agency/${payload.agencyId}/admin/${payload.userEmail}`,
                headers: {
                    'Content-Type': 'application/json',
                }
            }),
            invalidatesTags: ['Agencies', "KlimaLinkAdminUsers"],
            async onQueryStarted(_, {queryFulfilled, dispatch}) {
                try {
                    const response = await queryFulfilled
                    generalSuccessHandling(response.data, dispatch, SUCCESS_TYPES.UNMAKE_USER_FROM_AGENCY_ADMIN)
                } catch (error) {
                    generalErrorHandling(error, dispatch, ERROR_TYPES.UNMAKE_USER_FROM_AGENCY_ADMIN)
                }
            },
        }),
        createCompany: builder.mutation<GeneralBackendResponse, CreateCompanyRequest>({
            query: payload => ({
                method: 'POST',
                body: payload,
                url: "/api/v1/company",
                headers: {
                    'Content-Type': 'application/json',
                }
            }),
            invalidatesTags: ['Company', 'User'],
            async onQueryStarted(_, {queryFulfilled, dispatch}) {
                try {
                } catch (error) {
                    generalErrorHandling(error, dispatch, ERROR_TYPES.CREATE_COMPANY)
                }
            },
        }),
        updateCompany: builder.mutation<GeneralBackendResponse, UpdateCompanyRequest>({
            query: payload => ({
                method: 'PUT',
                body: payload,
                url: `/api/v1/company`,
                headers: {
                    'Content-Type': 'application/json',
                }
            }),
            invalidatesTags: ['Company'],
            async onQueryStarted(_, {queryFulfilled, dispatch}) {
                try {
                } catch (error) {
                    generalErrorHandling(error, dispatch, ERROR_TYPES.UPDATE_COMPANY)
                }
            },
        }),
        createAgency: builder.mutation<GeneralBackendResponse, CreateAgencyRequest>({
            query: payload => ({
                method: 'POST',
                body: payload,
                url: "/api/v1/agency",
                headers: {
                    'Content-Type': 'application/json',
                }
            }),
            invalidatesTags: ['Agencies', 'User'],
            async onQueryStarted(_, {queryFulfilled, dispatch}) {
                try {
                } catch (error) {
                    generalErrorHandling(error, dispatch, ERROR_TYPES.CREATE_AGENCY)
                }
            },
        }),
        updateAgency: builder.mutation<GeneralBackendResponse, UpdateAgencyRequest>({
            query: payload => ({
                method: "PUT",
                body: payload.agency,
                url: `/api/v1/agency/${payload.agencyId}`,
                headers: {
                    'Content-Type': 'application/json',
                }
            }),
            invalidatesTags: ['Agencies'],
            async onQueryStarted(_, {queryFulfilled, dispatch}) {
                try {
                    const response = await queryFulfilled
                    generalSuccessHandling(response.data, dispatch, SUCCESS_TYPES.UPDATE_AGENCY)
                } catch (error) {
                    generalErrorHandling(error, dispatch, ERROR_TYPES.UPDATE_AGENCY)
                }
            },
        }),
        inviteCompanyAdmin: builder.mutation<GeneralBackendResponse, InviteCompanyAdminRequest>({
            query: payload => ({
                method: "POST",
                url: '/api/v1/company/createWithUser',
                body: payload,
                headers: {
                    'Content-Type': 'application/json',
                }
            }),
            async onQueryStarted(_, {queryFulfilled, dispatch}) {
                try {
                    const response = await queryFulfilled
                    generalSuccessHandling(response.data, dispatch, SUCCESS_TYPES.INVITE_COMPANY_ADMIN)
                } catch (error) {
                    generalErrorHandling(error, dispatch, ERROR_TYPES.INVITE_COMPANY_ADMIN)
                }
            },
        }),
        inviteUsers: builder.mutation<GeneralBackendResponse, InviteUserRequest>({
            query: payload => ({
                method: "POST",
                url: `/api/v1/agency/${payload.agencyId}/member/add`,
                body: payload.users,
                headers: {
                    'Content-Type': 'application/json',
                }
            }),
            invalidatesTags: ['Agencies'],
            async onQueryStarted(_, {queryFulfilled, dispatch}) {
                try {
                    const response = await queryFulfilled
                    generalSuccessHandling(response.data, dispatch, SUCCESS_TYPES.INVITE_USER)
                } catch (error) {
                    generalErrorHandling(error, dispatch, ERROR_TYPES.INVITE_USER)
                }
            },
        }),
        deleteUser: builder.mutation<GeneralBackendResponse, DeleteUser>({
            query: payload => ({
                method: "DELETE",
                url: `/api/v1/userInfo/${payload.userEmail}`,
                headers: {
                    'Content-Type': 'application/json',
                }
            }),
            invalidatesTags: ['Agencies', 'KlimalinkAdminAgencies'],
            async onQueryStarted(_, {queryFulfilled, dispatch}) {
                try {
                    const response = await queryFulfilled
                    generalSuccessHandling(response.data, dispatch, SUCCESS_TYPES.DELETE_USER)
                } catch (error) {
                    generalErrorHandling(error, dispatch, ERROR_TYPES.DELETE_USER)
                }
            },
        }),
        removeUserFromAgency: builder.mutation<GeneralBackendResponse, RemoveUserFromAgency>({
            query: payload => ({
                method: "DELETE",
                url: `/api/v1/agency/${payload.agencyId}/member/${payload.userEmail}`,
                headers: {
                    'Content-Type': 'application/json',
                }
            }),
            invalidatesTags: ['Agencies', 'KlimalinkAdminAgencies'],
            async onQueryStarted(_, {queryFulfilled, dispatch}) {
                try {
                    const response = await queryFulfilled
                    generalSuccessHandling(response.data, dispatch, SUCCESS_TYPES.DELETE_USER)
                } catch (error) {
                    generalErrorHandling(error, dispatch, ERROR_TYPES.DELETE_USER)
                }
            },
        }),
        deleteAgency: builder.mutation<GeneralBackendResponse, number>({
            query: payload => ({
                method: "DELETE",
                url: `/api/v1/agency/${payload}`,
                headers: {
                    'Content-Type': 'application/json',
                }
            }),
            invalidatesTags: ['Agencies'],
            async onQueryStarted(_, {queryFulfilled, dispatch}) {
                try {
                    const response = await queryFulfilled
                    generalSuccessHandling(response.data, dispatch, SUCCESS_TYPES.DELETE_AGENCY)
                } catch (error) {
                    generalErrorHandling(error, dispatch, ERROR_TYPES.DELETE_AGENCY)
                }
            },
        }),
        setAgencyLogo: builder.mutation<GeneralBackendResponse, UploadLogoRequest>({
            query: payload => ({
                method: "POST",
                body: payload.logo,
                url: `/api/v1/agency/${payload.agencyId}/logo`,
            }),
            invalidatesTags: ['Agencies'],
            async onQueryStarted(_, {queryFulfilled, dispatch}) {
                try {
                    const response = await queryFulfilled
                    generalSuccessHandling(response.data, dispatch, SUCCESS_TYPES.SET_AGENCY_LOGO)
                } catch (error) {
                    generalErrorHandling(error, dispatch, ERROR_TYPES.SET_AGENCY_LOGO)
                }
            },
        }),
        deleteAgencyLogo: builder.mutation<GeneralBackendResponse, number>({
            query: payload => ({
                method: "DELETE",
                url: `/api/v1/agency/${payload}/logo`,
                headers: {
                    'Content-Type': 'application/json',
                }
            }),
            invalidatesTags: ['Agencies'],
            async onQueryStarted(_, {queryFulfilled, dispatch}) {
                try {
                    const response = await queryFulfilled
                    generalSuccessHandling(response.data, dispatch, SUCCESS_TYPES.DELETE_AGENCY_LOGO)
                } catch (error) {
                    generalErrorHandling(error, dispatch, ERROR_TYPES.DELETE_AGENCY_LOGO)
                }
            },
        }),
        changeUsersAgency: builder.mutation<GeneralBackendResponse, ChangeUsersAgencyRequest>({
            query: payload => ({
                method: "PUT",
                url: `/api/v1/agency/${payload.oldAgencyId}/${payload.newAgencyId}/member/${payload.userEmail}`,
            }),
            invalidatesTags: ['Agencies'],
            async onQueryStarted(_, {queryFulfilled, dispatch}) {
                try {
                    const response = await queryFulfilled
                    generalSuccessHandling(response.data, dispatch, SUCCESS_TYPES.CHANGE_USERS_AGENCY)
                } catch (error) {
                    generalErrorHandling(error, dispatch, ERROR_TYPES.CHANGE_USERS_AGENCY)
                }
            },
        }),
        sendChangePasswordEmail: builder.mutation<GeneralBackendResponse, void>({
            query: () => ({
                method: "POST",
                url: '/api/v1/user/me/password/reset',
            }),
            async onQueryStarted(_, {queryFulfilled, dispatch}) {
                try {
                    const response = await queryFulfilled
                    generalSuccessHandling(response.data, dispatch, SUCCESS_TYPES.CHANGE_PASSWORD_EMAIL)
                } catch (error) {
                    generalErrorHandling(error, dispatch, ERROR_TYPES.CHANGE_PASSWORD_EMAIL)
                }
            },
        }),
        getUsersAgencies: builder.query<AgencyProps[], void>({
            query: () => ({
                url: "/api/v1/agencies",
                headers: {
                    'Content-Type': 'application/json',
                }
            }),
            providesTags: ['Agencies'],
            transformResponse: (response: AgencyProps[]): AgencyProps[] => sortBy(response, response => response.name.toLowerCase()),
            async onQueryStarted(_, {dispatch, queryFulfilled}) {
                try {
                    const {data} = await queryFulfilled
                    if (data) {
                        dispatch(setAgencies(data))
                        const promises = data.map(async agency => {
                            const {id} = agency;
                            const agencyLogo = await getAgencyLogo(id);
                            if (agencyLogo?.status === GENERAL_BACKEND_RESPONSE.success) {
                                if (agencyLogo && agencyLogo.arrayBuffer && agencyLogo.arrayBuffer.byteLength > 0) {
                                    const {imageBlob, arrayBuffer, fileType} = agencyLogo
                                    dispatch(setAgencyLogo({
                                        agencyId: id,
                                        agencyLogo: {
                                            imageBlob: imageBlob!!,
                                            arrayBuffer: arrayBuffer!!,
                                            fileType: fileType!!
                                        }
                                    }))
                                }
                            }
                        });
                        await Promise.all(promises);
                    }
                } catch (error) {
                    console.error(`Something went wrong getting users agencies: ${JSON.stringify(error)}`)
                }
            },
        }),
        getUsersCompany: builder.query<CompanyProps[], void>({
            query: () => ({
                url: "/api/v1/company",
                headers: {
                    'Content-Type': 'application/json',
                },
            }),
            providesTags: ['Company'],
            async onQueryStarted(_, {dispatch, queryFulfilled}) {
                try {
                    const {data} = await queryFulfilled
                    if (data) dispatch(setCompany(data))
                } catch (error) {
                    console.error(`Something went wrong getting users company: ${JSON.stringify(error)}`)
                }
            },
        }),
        getUserInfoSelf: builder.query<UserProps, void>({
            query: () => ({
                url: "/api/v1/userInfo",
                headers: {
                    'Content-Type': 'application/json',
                }
            }),
            providesTags: ['User'],
            async onQueryStarted(_, {dispatch, queryFulfilled}) {
                try {
                    const {data} = await queryFulfilled
                    if (data) dispatch(setUserData(data))
                } catch (error) {
                    console.error(`Something went wrong getting user info: ${JSON.stringify(error)}`)
                }
            },
        }),
        getAllUsersForAdminUser: builder.query<UserProps[], void>({
            query: () => ({
                url: "/api/v1/userInfo/getUsers",
                headers: {
                    'Content-Type': 'application/json',
                }
            }),
            providesTags: ['Agencies'],
            transformResponse: (response: UserProps[]): UserProps[] => sortBy(response, response => response.firstName.toLowerCase()),
            async onQueryStarted(_, {dispatch, queryFulfilled}) {
                try {
                    const {data} = await queryFulfilled
                    if (data) dispatch(setAllUsersForAdmin(data))
                } catch (error) {
                    console.error(`Something went wrong getting all users for admin: ${JSON.stringify(error)}`)
                }
            },
        }),
        getAllAgenciesForKlimaLinkAdmin: builder.query<WindowedAgenciesResponse, ReadAgenciesWindowedRequest>({
            query: params => ({
                url: "/api/v1/userInfo/getAgenciesForKlimaLinkAdmin",
                method: "GET",
                params: {
                    ...(params.searchTerm && {searchTerm: params.searchTerm}),
                    ...(params.scrollPosition && {scrollPosition: params.scrollPosition}),
                    direction: params.direction,
                    size: params.size,
                    sort: params.sort,
                    sortOrder: params.sortOrder
                },
                headers: {
                    'Content-Type': 'application/json',
                }
            }),
        }),
        getAllUsersForKlimaLinkAdmin: builder.query<WindowedUsersResponse, ReadUsersWindowedRequest>({
            query: params => ({
                url: "/api/v1/userInfo/getUsersForKlimaLinkAdmin",
                method: "GET",
                params: {
                    ...(params.searchTerm && {searchTerm: params.searchTerm}),
                    ...(params.scrollPosition && {scrollPosition: params.scrollPosition}),
                    direction: params.direction,
                    size: params.size,
                    sort: params.sort,
                    sortOrder: params.sortOrder
                }

            }),
            providesTags: ["KlimaLinkAdminUsers"]
        }),
        getAllAgenciesForGivenUser: builder.query<GetAgenciesForGivenUserResponse, string>({
            query: payload => ({
                url: `/api/v1/userInfo/getAgenciesForGivenUser/${payload}`,
                method: "GET",
                headers: {
                    'Content-Type': 'application/json',
                }
            }),
        }),
        // Hotel (there are more flight related endpoints in <AutocompleteQuery /> and <ChooseHotel />)
        getHotelByGiataId: builder.query<HotelResponse | null, string>({
            query: payload => ({
                method: "GET",
                url: `/api/v1/hotel/queryHotelByGiataId?query=${payload}`,
                headers: {
                    'Content-Type': 'application/json',
                }
            }),
            async onQueryStarted(_, {dispatch, queryFulfilled}) {
                try {
                } catch (error) {
                    generalErrorHandling(error, dispatch, ERROR_TYPES.GET_HOTEL_BY_GIATA_ID)
                }
            },
        }),

        // Car & Bus
        getAddressWithCoordinates: builder.query<GetAdressWithCoordinatesResponse | null, GetAddressWithCoordinatesRequest>({
            query: payload => ({
                method: "GET",
                url: `/api/v1/car/getAddressFromCoordinates?language=${payload.language}&longitude=${payload.longitude}&latitude=${payload.latitude}`,
                headers: {
                    'Content-Type': 'application/json',
                }
            }),
            async onQueryStarted(_, {queryFulfilled, dispatch}) {
                try {
                } catch (error) {
                    generalErrorHandling(error, dispatch, ERROR_TYPES.GENERAL_MAPBOX_ERROR)
                }
            },
        }),
        getCarCalculatorProps: builder.query<CarCalculatorPropsResponse, void>({
            query: () => ({
                method: "GET",
                url: '/api/v1/car/getAllCarCalculatorProps',
                headers: {
                    'Content-Type': 'application/json',
                }
            }),
        }),
        validateAcrissCode: builder.mutation<ValidateAcrissCodeResponse, AcrissCodeRequest>({
            query: payload => ({
                method: "POST",
                body: payload,
                url: '/api/v1/car/validateAcrissCode',
                headers: {
                    'Content-Type': 'application/json',
                }
            }),
            async onQueryStarted(_, {queryFulfilled, dispatch}) {
                try {
                } catch (error) {
                    generalErrorHandling(error, dispatch, ERROR_TYPES.VALIDATE_ACRISS_CODE)
                }
            },
        }),
        getCarPropsViaAcrissCode: builder.mutation<CarPropsViaAcrissCodeResponse, AcrissCodeRequest>({
            query: payload => ({
                method: "POST",
                body: payload,
                url: '/api/v1/car/getAcrissCodeMappedDetails',
                headers: {
                    'Content-Type': 'application/json',
                }
            }),
            async onQueryStarted(_, {queryFulfilled, dispatch}) {
                try {
                } catch (error) {
                    generalErrorHandling(error, dispatch, ERROR_TYPES.CAR_PROPS_VIA_ACRISS_CODE)
                }
            },
        }),
        getRouteFromCoordinates: builder.mutation<GetRouteFromCoordinatesResponse, GetRouteFromCoordinatesRequest[]>({
            query: payload => ({
                method: "POST",
                body: payload,
                url: '/api/v1/car/getRouteFromCoordinates',
                headers: {
                    'Content-Type': 'application/json',
                }
            }),
            transformResponse: (response: GetRouteFromCoordinatesResponse) => {
                response.routes.forEach(route => route.geometry = polyline.toGeoJSON(route.geometry))
                return response
            },
            async onQueryStarted(_, {queryFulfilled, dispatch}) {
                try {
                } catch (error) {
                    generalErrorHandling(error, dispatch, ERROR_TYPES.ROUTE_FROM_COORDINATES)
                }
            },
        }),
        getComputedTotalPassengerCount: builder.query<GetComputedTotalPassengerCountResponse, GetComputedTotalPassengerCountRequest>({
            query: payload => ({
                method: "GET",
                url: `/api/v1/car/getComputedTotalPassengerCount?busCategory=${payload.busCategory}`,
                headers: {
                    'Content-Type': 'application/json',
                }
            }),
            async onQueryStarted(_, {queryFulfilled, dispatch}) {
                try {
                } catch (error) {
                    generalErrorHandling(error, dispatch, ERROR_TYPES.COMPUTED_TOTAL_PASSENGER_COUNT)
                }
            },
        }),

        // Flight (there are more flight related endpoints in <AutocompleteQuery /> and <ChooseFlight />)
        getAirlines: builder.query<AirlineResponse[], string>({
            query: payload => ({
                method: "GET",
                url: `${GET_AIRLINES}?query=${payload}`,
                headers: {
                    'Content-Type': 'application/json',
                }
            }),
        }),

        // Bundle
        deleteBundle: builder.mutation<GeneralBackendResponse, number>({
            query: payload => {
                return {
                    method: 'DELETE',
                    url: `/api/v1/bundle/delete/${payload}`,
                    headers: {
                        'Content-Type': 'application/json',
                    }
                }
            },
            async onQueryStarted(_, {queryFulfilled, dispatch}) {
                try {
                    const response = await queryFulfilled
                    generalSuccessHandling(response.data, dispatch, SUCCESS_TYPES.DELETE_BUNDLE)
                } catch (error) {
                    generalErrorHandling(error, dispatch, ERROR_TYPES.DELETE_BUNDLE)
                }
            },
        }),
        getCustomerOnSavingBundle: builder.query<Customer[], SaveBundleCustomerRequest>({
            query: payload => ({
                method: "GET",
                url: `/api/v1/customer`,
                body: payload,
                headers: {
                    'Content-Type': 'application/json',
                }
            }),
        }),
        createBundle: builder.mutation<GeneralBackendResponse, CreateBundleProps>({
            query: payload => {
                return {
                    method: 'POST',
                    body: transformFrontEndBundleToBackendBundle(payload),
                    url: "/api/v1/bundle/create",
                    headers: {
                        'Content-Type': 'application/json',
                    }
                }
            },
            async onQueryStarted(_, {queryFulfilled, dispatch}) {
                try {
                    const response = await queryFulfilled
                    generalSuccessHandling(response.data, dispatch, SUCCESS_TYPES.CREATE_BUNDLE)
                } catch (error) {
                    generalErrorHandling(error, dispatch, ERROR_TYPES.CREATE_BUNDLE)
                }
            },
        }),
        readBundles: builder.query<TransformedWindowedBundleResponse, ReadBundleWindowedRequest>({
            query: (params) => ({
                url: '/api/v1/bundle/read',
                method: 'GET',
                params: {
                    ...(params.scrollPosition && {scrollPosition: params.scrollPosition}),
                    ...(params.searchTerm && {searchTerm: params.searchTerm}),
                    agencyId: params.agencyId,
                    size: params.size,
                    sortOrder: params.sortOrder,
                    direction: params.direction,
                    sort: params.sort,
                    whatToGet: params.whatToGet,
                },
                headers: {
                    'Content-Type': 'application/json',
                },
            }),
            transformResponse: (response: WindowedBundleResponse) => {
                const {bundles, nextPosition, bundlesCount} = response
                const updatedBundles = bundles.map(bundle => {
                    const {startDate, endDate, updated, totalCo2KgE, created} = bundle
                    return {
                        ...bundle,
                        startDate: startDate ? DateTime.fromISO(startDate) : null,
                        endDate: endDate ? DateTime.fromISO(endDate) : null,
                        updated: updated ? DateTime.fromISO(updated) : null,
                        created: DateTime.fromISO(created),
                        totalCo2KgE: totalCo2KgE ? roundCo2E(totalCo2KgE) : null
                    }
                })
                return {
                    bundles: updatedBundles,
                    nextPosition,
                    bundlesCount
                }
            },
            async onQueryStarted(_, {dispatch, queryFulfilled}) {
                try {
                } catch (error) {
                    generalErrorHandling(error, dispatch, ERROR_TYPES.READ_BUNDLE)
                }
            },
        }),
        loadBundle: builder.query<BundleForStore, number>({
            query: payload => ({
                url: `/api/v1/bundle/load/${payload}`,
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            }),
            transformResponse: (response: LoadBundleResponse) => transformBackendBundleToFrontEndBundle(response),
            async onQueryStarted(_, {dispatch, queryFulfilled}) {
                try {
                } catch (error) {
                    generalErrorHandling(error, dispatch, ERROR_TYPES.LOAD_BUNDLE)
                }
            },
        }),
        updateBundle: builder.mutation<GeneralBackendResponse, UpdateBundle>({
            query: payload => {
                return {
                    method: 'PUT',
                    body: transformFrontEndBundleToBackendBundle(payload),
                    url: "/api/v1/bundle/update",
                    headers: {
                        'Content-Type': 'application/json',
                    }
                }
            },
            async onQueryStarted(_, {queryFulfilled, dispatch}) {
                try {
                    const response = await queryFulfilled
                    generalSuccessHandling(response.data, dispatch, SUCCESS_TYPES.UPDATE_BUNDLE)
                } catch (error) {
                    generalErrorHandling(error, dispatch, ERROR_TYPES.UPDATE_BUNDLE)
                }
            },
        }),
    })
})

export const {
    useResendInvitationEmailMutation,
    useGetUserInfoSelfQuery,
    useGetUsersAgenciesQuery,
    useGetUsersCompanyQuery,
    useGetAllUsersForAdminUserQuery,
    useLazyGetAllAgenciesForKlimaLinkAdminQuery,
    useLazyGetAllUsersForKlimaLinkAdminQuery,
    useLazyGetAllAgenciesForGivenUserQuery,
    useUpdateAgencyMutation,
    useUpdateCompanyMutation,
    useRemoveUserFromAgencyMutation,
    useDeleteUserMutation,
    useDeleteAgencyMutation,
    useMakeUserToAgencyAdminMutation,
    useUnmakeUserFromAgencyAdminMutation,
    useUpdateUserInfoAdminMutation,
    useUpdateUserInfoSelfMutation,
    useCreateCompanyMutation,
    useCreateAgencyMutation,
    useSetAgencyLogoMutation,
    useDeleteAgencyLogoMutation,
    useInviteCompanyAdminMutation,
    useInviteUsersMutation,
    useChangeUsersAgencyMutation,
    useSendChangePasswordEmailMutation,
    useCreateBundleMutation,
    useLazyReadBundlesQuery,
    useLazyLoadBundleQuery,
    useCalculateEmissionsMutation,
    useUpdateBundleMutation,
    useDeleteBundleMutation,
    useGetRouteFromCoordinatesMutation,
    useValidateAcrissCodeMutation,
    useGetCarCalculatorPropsQuery,
    useGetCarPropsViaAcrissCodeMutation,
    useLazyGetComputedTotalPassengerCountQuery
} = apiSlice


